'use client'

import React, { useState } from 'react'
import { faSpinner, faTrash, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { logOut, logOutAndClearReminder } from 'features/authSlice'
import { useAppDispatch } from 'features/hooks'
import { RootState } from 'features/store'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  useDeleteAccountMutation,
  useDeleteSingleListingMutation,
  useDeleteAllListingsMutation,
  useDeactivateListingMutation,
  useReactivateListingMutation,
} from 'graphql/account-deletion'

interface DeleteAccountDialogProps {
  handleDeleteOpen: () => void
}

type DeletionStep = 'choose' | 'confirm-account' | 'confirm-listing' | 'confirm-all-listings'

const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({ handleDeleteOpen }) => {
  const dispatch = useAppDispatch()
  const user = useSelector((state: RootState) => state.culineerAuth.user)
  const [step, setStep] = useState<DeletionStep>('choose')
  const [selectedListingId, setSelectedListingId] = useState<number | null>(null)
  const [confirmText, setConfirmText] = useState('')
  const [password, setPassword] = useState('')

  const [deleteAccount, { loading: deleteAccountLoading }] = useDeleteAccountMutation()
  const [deleteSingleListing, { loading: deleteListingLoading }] = useDeleteSingleListingMutation()
  const [deleteAllListings, { loading: deleteAllLoading }] = useDeleteAllListingsMutation()
  const [deactivateListing, { loading: deactivateLoading }] = useDeactivateListingMutation()
  const [reactivateListing, { loading: reactivateLoading }] = useReactivateListingMutation()

  const isBusinessUser = user?.user_type === 'business' || (user?.listings && user.listings.length > 0)
  const listings = user?.listings || []

  const handleDeleteAccount = async () => {
    if (confirmText !== 'DELETE' || !password) return
    try {
      const { data } = await deleteAccount({
        variables: { password },
      })
      if (data?.deleteAccount.success) {
        toast.success(data.deleteAccount.message)
        handleDeleteOpen()
        dispatch(logOutAndClearReminder())
      } else {
        toast.error(data?.deleteAccount.message || 'Failed to delete account')
      }
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong')
    }
  }

  const handleDeleteSingleListing = async () => {
    if (!selectedListingId) return
    try {
      const { data } = await deleteSingleListing({
        variables: { listingId: selectedListingId },
      })
      if (data?.deleteSingleListing.success) {
        toast.success(data.deleteSingleListing.message)
        handleDeleteOpen()
      } else {
        toast.error(data?.deleteSingleListing.message || 'Failed to delete listing')
      }
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong')
    }
  }

  const handleDeleteAllListings = async () => {
    if (confirmText !== 'DELETE ALL') return
    try {
      const { data } = await deleteAllListings()
      if (data?.deleteAllListings.success) {
        toast.success(data.deleteAllListings.message)
        handleDeleteOpen()
      } else {
        toast.error(data?.deleteAllListings.message || 'Failed to delete listings')
      }
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong')
    }
  }

  const handleDeactivateListing = async (listingId: number) => {
    try {
      const { data } = await deactivateListing({
        variables: { listingId },
      })
      if (data?.deactivateListing.success) {
        toast.success(data.deactivateListing.message)
      } else {
        toast.error(data?.deactivateListing.message || 'Failed to deactivate listing')
      }
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong')
    }
  }

  const handleReactivateListing = async (listingId: number) => {
    try {
      const { data } = await reactivateListing({
        variables: { listingId },
      })
      if (data?.reactivateListing.success) {
        toast.success(data.reactivateListing.message)
      } else {
        toast.error(data?.reactivateListing.message || 'Failed to reactivate listing')
      }
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong')
    }
  }

  // Step: Choose what to delete
  if (step === 'choose') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          We really hate to see you go. We&apos;ll preserve your data for{' '}
          {isBusinessUser ? '90' : '30'} days in case you change your mind.
          If you decide to come back, create an account with the same email and
          we&apos;ll restore your content. After that it&apos;ll be gone forever.
        </p>

        {/* Business users: Listing management options */}
        {isBusinessUser && listings.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-900">Manage this Account</h4>

            {/* Individual listing actions */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {listings.map((listing: any) => (
                <div
                  key={listing.listing_id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-xl"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {listing.name || 'Unnamed listing'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {listing.city}{listing.state ? `, ${listing.state}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Delete all listings */}
            {listings.length > 1 && (
              <button
                type="button"
                onClick={() => {
                  setStep('confirm-all-listings')
                  setConfirmText('')
                }}
                className="w-full flex items-center justify-center gap-2 p-3 border border-orange-200 bg-orange-50 text-orange-700 rounded-xl hover:bg-orange-100 transition-colors text-sm font-medium"
              >
                <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                Delete all listings
              </button>
            )}

            <div className="border-t border-gray-200 my-3" />
          </div>
        )}

        {/* Delete entire account */}
        <button
          type="button"
          onClick={() => {
            setStep('confirm-account')
            setConfirmText('')
          }}
          className="w-full flex items-center justify-center gap-2 p-3 border border-red-200 bg-red-50 text-red-700 rounded-xl hover:bg-red-100 transition-colors text-sm font-medium"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="h-3.5 w-3.5" />
          Delete my entire account
        </button>

        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={handleDeleteOpen}
            className="rounded-full py-2 px-6 text-sm text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  // Step: Confirm account deletion
  if (step === 'confirm-account') {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl">
          <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-red-800">This action cannot be easily undone</p>
            <p className="text-xs text-red-600">
              Your account and all associated data (listings, posts, followers) will be deleted.
              {user?.subscription_status === 'active' && ' Your subscription will be cancelled immediately.'}
              {' '}You have {isBusinessUser ? '90' : '30'} days to contact support to recover your account.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type <span className="font-mono font-bold">DELETE</span> to confirm
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            className="w-full h-12 px-4 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Enter your password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your current password"
            autoComplete="new-password"
            className="w-full h-12 px-4 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400"
          />
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => setStep('choose')}
            className="rounded-full py-2 px-6 text-sm text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleDeleteAccount}
            disabled={confirmText !== 'DELETE' || !password || deleteAccountLoading}
            className="rounded-full py-2.5 px-6 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {deleteAccountLoading ? (
              <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
            ) : (
              'Delete Account'
            )}
          </button>
        </div>
      </div>
    )
  }

  // Step: Confirm single listing deletion
  if (step === 'confirm-listing') {
    const selectedListing = listings.find((l: any) => l.listing_id === selectedListingId)
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-800">
              Delete &quot;{selectedListing?.name || 'this listing'}&quot;?
            </p>
            <p className="text-xs text-amber-700">
              This listing will be removed from search results and your profile.
              You have 30 days to contact support to recover it. Your account will remain active.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => setStep('choose')}
            className="rounded-full py-2 px-6 text-sm text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleDeleteSingleListing}
            disabled={deleteListingLoading}
            className="rounded-full py-2.5 px-6 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {deleteListingLoading ? (
              <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
            ) : (
              'Delete Listing'
            )}
          </button>
        </div>
      </div>
    )
  }

  // Step: Confirm delete all listings
  if (step === 'confirm-all-listings') {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-200 rounded-xl">
          <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-orange-800">
              Delete all {listings.length} listing{listings.length > 1 ? 's' : ''}?
            </p>
            <p className="text-xs text-orange-700">
              All your listings will be removed. Your account will remain active but you won&apos;t have any business listings.
              {user?.subscription_status === 'active' && ' Your subscription will be cancelled.'}
              {' '}You have 30 days to contact support to recover them.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type <span className="font-mono font-bold">DELETE ALL</span> to confirm
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE ALL"
            className="w-full h-12 px-4 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
          />
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => setStep('choose')}
            className="rounded-full py-2 px-6 text-sm text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleDeleteAllListings}
            disabled={confirmText !== 'DELETE ALL' || deleteAllLoading}
            className="rounded-full py-2.5 px-6 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {deleteAllLoading ? (
              <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
            ) : (
              'Delete All Listings'
            )}
          </button>
        </div>
      </div>
    )
  }

  return null
}

export default DeleteAccountDialog
